import React from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { LanguageProvider, useLanguage } from './i18n';
import Header from './components/Header';
import HomePage from './components/HomePage';
import LegalPage from './components/LegalPage';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import DrillPage from './components/DrillPage';
import DrillResultsPage from './components/DrillResultsPage';
import ContentTestingPage from './components/ContentTestingPage';
import LinkedInInsightTag from './components/LinkedInInsightTag';
import DocumentHead from './components/DocumentHead';
import NotFound from './components/NotFound';
import SavedLanguageRedirect from './components/SavedLanguageRedirect';
import { LEGAL_REDIRECTS, LEGAL_ROUTE_PATHS } from './legal/routes';
import { CONTENT_TESTING_ROUTE_PATHS } from './content-testing/routes';

/**
 * TamerinTECH landing page
 *
 * Light, editorial product page led by ContextFriend (see HomePage for the
 * section order). Shared across routes: sticky header, footer, the website
 * chatbot, the LinkedIn Insight Tag, English/German copy and the EU legal
 * pages. The content-testing subpages and the internal drill pages keep their
 * own layouts. Unknown paths render a not-found page; the host returns 404
 * for addresses that were not prerendered.
 */
const StripTrailingSlash: React.FC = () => {
  const location = useLocation();
  if (location.pathname.length > 1 && location.pathname.endsWith('/')) {
    return (
      <Navigate
        to={`${location.pathname.replace(/\/+$/, '')}${location.search}${location.hash}`}
        replace
      />
    );
  }
  return null;
};

const isDrillPath = (pathname: string) =>
  pathname === '/drill-1' || pathname.startsWith('/drill-1/');

export const AppRoutes: React.FC = () => {
  const location = useLocation();
  const { t } = useLanguage();

  if (isDrillPath(location.pathname)) {
    return (
      <>
        <StripTrailingSlash />
        <ScrollToTop />
        <Routes>
          <Route path="/drill-1" element={<DrillPage />} />
          <Route path="/drill-1/ergebnisse" element={<DrillResultsPage />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </>
    );
  }

  return (
    <>
      <StripTrailingSlash />
      <ScrollToTop />
      <LinkedInInsightTag />
      <div className="lp-page min-h-screen bg-white text-gray-900">
        <a href="#main-content" className="lp-skip">
          {t.header.skip}
        </a>
        <Header />
        <main id="main-content" className="overflow-x-clip">
          <Routes>
            {LEGAL_REDIRECTS.map(({ from, to }) => (
              <Route key={from} path={from} element={<Navigate to={to} replace />} />
            ))}
            {LEGAL_ROUTE_PATHS.map((path) => (
              <Route key={path} path={path} element={<LegalPage />} />
            ))}
            {CONTENT_TESTING_ROUTE_PATHS.map((path) => (
              <Route key={path} path={path} element={<ContentTestingPage />} />
            ))}
            <Route path="/" element={<HomePage />} />
            <Route path="/de" element={<HomePage />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
        <Footer />
      </div>
    </>
  );
};

/** Router-agnostic tree. The browser entry and the prerender entry each supply a router. */
export const AppContent: React.FC = () => (
  <LanguageProvider>
    <DocumentHead />
    <SavedLanguageRedirect />
    <AppRoutes />
  </LanguageProvider>
);

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
};

export default App;
